import { dateFormatterFactory, DateTrunc, FiscalYearStartMonth, parseDataPoints, validDateOrUndefined } from '@tableau/metrics-engine';
import { MetricsBaseProps, defaultComparisonFor } from '@tableau/metrics-shared-widgets-dom/';
import { MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricDetailView';
import { SpacesSiteUrls } from '../app/spaces/SpacesSiteUrls';
import { IMetricDataInfo, IMetricInfo } from './models/IMetricInfo';
import { DateGranularity, IMetricDataList } from '../ts/ServerApi/types/IMetricDataList';

export const metricCardProps = (metric: IMetricDataInfo, metricInfo?: IMetricInfo): MetricsBaseProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const measureFormat = metric.schema ? metric.schema.measure.format : '';
  const dateTrunc = dateTruncFromSchema(metricInfo);
  const comparison = defaultComparisonFor(dateTrunc);
  return {
    testId: 'metric-card',
    data: data.slice(-100),
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    measureFormat,
    suspended: false,
    comparison
  };
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];

  if (data) {
    return { ...metricInfo,
      dataPoints: parseDataPoints(data.points),
      schema: data.schema,
    dataRefreshedTimestamp: data.dataRefreshedTimestamp };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: IMetricDataInfo): number => {
  return metric && metric.dataPoints && metric.dataPoints.length > 0 && metric.dataPoints[metric.dataPoints.length - 1].measure;
};

export const getMeasureFormat = (metric: IMetricDataInfo): string | undefined => {
  return metric && metric.schema && metric.schema.measure.format;
};

export const metricDetailProps = (metric: IMetricInfo, urls: SpacesSiteUrls): MetricDetailViewProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const dateTrunc = dateTruncFromSchema(metric);
  const fiscalYearStartMonth = metric.schema?.timestamp?.fiscalYearStartMonth || FiscalYearStartMonth.JANUARY;
  const dateFormatter = dateFormatterFactory(dateTrunc, fiscalYearStartMonth);
  const comparison = defaultComparisonFor(dateTrunc);

  return {
    metricName: metric.name,
    testId: 'metric-detail',
    data,
    dateFormatter,
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    measureFormat: metric.schema ? metric.schema.measure.format : '',
    comparison
  };
};

export const dateTruncFromGranularity = (date?: DateGranularity): DateTrunc => {
  switch (date) {
    case DateGranularity.SECOND: {
      return DateTrunc.SECOND;
    }
    case DateGranularity.MINUTE: {
      return DateTrunc.MINUTE;
    }
    case DateGranularity.HOUR: {
      return DateTrunc.HOUR;
    }
    case DateGranularity.DAY: {
      return DateTrunc.DAY;
    }
    case DateGranularity.ISO_WEEKDAY: {
      return DateTrunc.ISO_WEEKDAY;
    }
    case DateGranularity.WEEK: {
      return DateTrunc.WEEK;
    }
    case DateGranularity.ISO_WEEK: {
      return DateTrunc.ISO_WEEK;
    }
    case DateGranularity.MONTH: {
      return DateTrunc.MONTH;
    }
    case DateGranularity.QUARTER: {
      return DateTrunc.QUARTER;
    }
    case DateGranularity.ISO_QUARTER: {
      return DateTrunc.ISO_QTR;
    }
    case DateGranularity.YEAR: {
      return DateTrunc.YEAR;
    }
    case DateGranularity.ISO_YEAR: {
      return DateTrunc.ISO_YEAR;
    }
    case DateGranularity.UNKNOWN:
    case undefined:
    default: {
      return DateTrunc.UNKNOWN;
    }
  }
};

function dateTruncFromSchema(metric: IMetricInfo) {
  return metric.schema && metric.schema.timestamp ? dateTruncFromGranularity(metric.schema.timestamp.dateGranularity) : DateTrunc.UNKNOWN;
}
